import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';

interface Merchant {
  id: number;
  name: string;
  email: string;
  balance?: string | number;
  account_balance?: string | number;
  created_by?: number;
}

interface ManagerTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransferSuccess?: () => void;
}

const ManagerTransferModal: React.FC<ManagerTransferModalProps> = ({
  isOpen,
  onClose,
  onTransferSuccess
}) => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<number | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [managerBalance, setManagerBalance] = useState<number>(0);

  const fetchManagerBalance = async () => {
    try {
      // Fetch manager's account_balance from user_wallet table
      const response = await api.get('/auth/profile');
      console.log('Full profile response:', response.data);
      
      if (response.data.success) {
        const balance = response.data.data.account_balance || 0;
        console.log('Raw account_balance from API:', response.data.data.account_balance);
        console.log('Parsed balance:', parseFloat(balance));
        setManagerBalance(parseFloat(balance));
        console.log('Manager account balance set to:', parseFloat(balance));
      } else {
        console.log('Profile API failed:', response.data);
        setManagerBalance(0);
      }
    } catch (error) {
      console.error('Failed to fetch manager account balance:', error);
      setManagerBalance(0);
    }
  };

  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, fetching merchants from API...');
      
      // Fetch manager balance
      fetchManagerBalance();
      
      // Fetch merchants from API
      fetchMerchants();
    }
  }, [isOpen]);

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching merchants from /dashboard/manager/merchants...');
      
      const response = await api.get('/dashboard/manager/merchants');
      console.log('Merchants API response:', response.data);
      
      if (response.data.success && response.data.data) {
        const merchants = response.data.data;
        console.log('Successfully fetched merchants with account_balance:', merchants);
        setMerchants(merchants);
      } else {
        throw new Error('Invalid response format');
      }
      
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
      setError('Failed to load merchants');
      
      // Fallback data if API fails
      const fallbackMerchants = [
        { id: 8, name: 'Merchant Ravi', email: 'merchant@yuvanapi.com', account_balance: '0.00', created_by: 7 },
        { id: 9, name: 'Amit Merchant', email: 'merchant2@yuvanapi.com', account_balance: '0.00', created_by: 7 },
        { id: 25, name: 'Krish Merchant', email: 'merchant3@yuvanapi.com', account_balance: '0.00', created_by: 7 }
      ];
      setMerchants(fallbackMerchants);
      console.log('Using fallback merchants:', fallbackMerchants);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedMerchant || !amount) {
      setError('Please select a merchant and enter an amount');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the new transfer endpoint
      const response = await api.post('/dashboard/manager/transfer-funds', {
        merchant_id: selectedMerchant,
        amount: transferAmount
      });

      if (response.data.success) {
        setSuccess('Funds transferred successfully!');
        setAmount('');
        setSelectedMerchant(null);
        
        // Call success callback after a delay
        setTimeout(() => {
          onTransferSuccess?.();
          onClose();
        }, 2000);
      } else {
        setError(response.data.message || 'Transfer failed');
      }
    } catch (error: any) {
      console.error('Transfer error:', error);
      setError(error.response?.data?.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setSelectedMerchant(null);
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div id="credit-money-modal-overlay" className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999] p-5 backdrop-blur-sm" onClick={handleClose} aria-modal="true" role="dialog">
      <div id="credit-money-modal-content" className="bg-slate-800 rounded-xl max-w-lg w-full min-w-[320px] max-h-[90vh] flex flex-col shadow-2xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center py-4 px-5 border-b border-white/[0.1] bg-slate-700/60 shrink-0">
          <h2 className="text-lg font-bold text-slate-100 m-0">Credit Money</h2>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 text-2xl" onClick={handleClose}>×</button>
        </div>

        <div className="p-6 overflow-y-auto min-h-0">
          {success && (
            <div className="mb-4 py-3 px-4 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-200 text-sm font-medium">
              ✅ {success}
            </div>
          )}

          {error && (
            <div className="mb-4 py-3 px-4 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm font-medium">
              ❌ {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="py-3 px-4 rounded-xl bg-slate-700/40 border border-white/[0.08]">
              <p className="text-slate-400 text-xs font-medium m-0 mb-1">Available balance</p>
              <p className="text-slate-100 font-bold text-lg m-0">{Math.floor(managerBalance).toLocaleString()} Coin</p>
              <small className="text-sm text-slate-400 italic m-0 block mt-1">This amount will be deducted from your wallet</small>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="merchant-select" className="font-semibold text-slate-300 text-sm">Select Merchant *</label>
              <select
                id="merchant-select"
                value={selectedMerchant || ''}
                onChange={(e) => setSelectedMerchant(Number(e.target.value))}
                className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                required
                disabled={loading}
              >
                <option value="">Choose a merchant...</option>
                {merchants.map((merchant) => {
                  const displayText = `Merchant - ${merchant.name || 'Unknown'} (${merchant.email || 'No Email'})`;
                  return (
                    <option key={merchant.id} value={merchant.id}>
                      {displayText}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="amount" className="font-semibold text-slate-300 text-sm">Number of Coins to Send *</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full py-2.5 px-3.5 border border-white/[0.12] rounded-lg text-sm font-medium text-slate-200 bg-slate-800/80 placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter number of coins"
                min={0.01}
                step={0.01}
                required
                disabled={loading}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-white/[0.1]">
              <button
                type="button"
                onClick={handleClose}
                className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="py-2.5 px-5 rounded-lg font-semibold text-sm cursor-pointer bg-gradient-to-r from-violet-500 to-violet-600 text-white border-none hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={loading || !selectedMerchant || !amount}
              >
                {loading ? 'Sending...' : 'Send Coins'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ManagerTransferModal;
